import React, { useEffect } from 'react';
import { Box } from 'edenred-ui';
import { useFormContext } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import { useTheme } from 'styled-components';

import { EmployeeTypeModes } from '@epi-constants/beneficiary';
import {
  FirstNameField,
  LastNameField,
  EmailAddressField
} from '@epi-forms/controls';
import { FormFields } from '@epi-forms/helpers';
import { isAfterToday, parseStringToDate } from '@epi-helpers/dateHelper';
import {
  maxLength,
  socialSecurityNumber,
  required as requiredValidation
} from '@epi-helpers/formHelpers/validators';
import { TerminationDate } from '@epi-pages/Beneficiaries/components/TerminationDate';

import { ContractPercentageField } from '../ContractPercentageField';
import { DaysForLunariField } from '../DaysForLunariField';
import { EmployeeTypeDataField } from '../EmployeeTypeDataField';
import { EntitledDataField } from '../EntitledDataField';
import { PersonalDataField } from '../PersonalDataField';
import { EndOfFundsField } from '../EndOfFundsField';

type OwnProps = {
  isAdd: boolean;
};
export const PersonalData = ({ isAdd }: OwnProps) => {
  const { t } = useTranslation();
  const { watch, clearErrors } = useFormContext();
  const isLunchBenefitActive = watch(FormFields.LunchBenefitActive);
  const EoF = watch(FormFields.EndOfFunds);
  const EoB = watch(FormFields.EndOfBenefit);
  const employeeType = watch(FormFields.EmployeeType);
  const isPartTimeOrTemporaryType = [
    EmployeeTypeModes.PartTime,
    EmployeeTypeModes.TemporaryEmployed,
    ''
  ].includes(employeeType);
  const isOnHold = employeeType === EmployeeTypeModes.Onhold;
  const isDropped = employeeType === EmployeeTypeModes.Dropped;
  const isPartTime = employeeType === EmployeeTypeModes.PartTime;
  const { sizeExtraSmall } = useTheme();
  const endOfFundsDatePassed = !!EoF && !!isAfterToday(parseStringToDate(EoF));

  useEffect(() => {
    if (!EoB) {
      clearErrors(FormFields.EndOfFunds);
    }
  }, [EoB]);

  return (
    <Box
      p={0}
      pt={4}
      display="flex"
      flexDirection="column"
      gap={3}
      width="100%"
      id="employee-benefit-personal-data-tab"
    >
      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        columnGap={6}
        rowGap={4}
      >
        <FirstNameField readOnly={endOfFundsDatePassed} />
        <LastNameField readOnly={endOfFundsDatePassed} />

        <PersonalDataField
          label={t('containers.beneficiaries.detail.personal_id')}
          name={FormFields.PersonalID}
          required={isAdd}
          validate={
            isAdd
              ? [requiredValidation, maxLength(50), socialSecurityNumber]
              : undefined
          }
          disabled={!isAdd || endOfFundsDatePassed}
        />
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        columnGap={6}
        rowGap={4}
      >
        <EmailAddressField
          label={t('containers.beneficiaries.table.email')}
          readOnly={endOfFundsDatePassed}
          shouldClearErrors={false}
        />

        <EntitledDataField isRemoved={EoF && !isAdd} isAddMode={isAdd} />
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        columnGap={6}
        rowGap={4}
      >
        <Box>
          <EmployeeTypeDataField isAddMode={isAdd} />
          {isOnHold && isAdd && (
            <Box
              mt={1}
              fontSize={sizeExtraSmall}
              id="add_beneficiary_employee_type_subtext"
            >
              {t('containers.beneficiaries.detail.employee_type_subtext')}
            </Box>
          )}
        </Box>

        <Box>
          <ContractPercentageField
            disabled={!isPartTimeOrTemporaryType || endOfFundsDatePassed}
            isAddMode={isAdd}
            isPartTime={isPartTime}
          />
          <Box mt={1} fontSize={12} id="add_beneficiary_working_hours_subtext">
            {t('containers.beneficiaries.detail.working_hours_subtext')}
          </Box>
        </Box>

        {isLunchBenefitActive ? (
          <Box>
            <DaysForLunariField
              disabled={(isOnHold || isDropped) && !isAdd}
              isAddMode={isAdd}
            />
            <Box
              mt={1}
              fontSize={sizeExtraSmall}
              id="add_beneficiary_lunch_benefit_per_month_subtext"
            >
              <Trans
                defaults={t(
                  'containers.beneficiaries.detail.lunch_benefit_per_month_subtext'
                )}
                components={{ b: <b /> }}
              />
            </Box>
          </Box>
        ) : (
          <Box />
        )}
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        columnGap={6}
        rowGap={4}
      >
        <PersonalDataField
          label={t('containers.beneficiaries.detail.employee_number')}
          name={FormFields.EmployeeNumber}
          validate={[maxLength(20)]}
          disabled={endOfFundsDatePassed}
        />

        <PersonalDataField
          label={t('containers.beneficiaries.detail.cost_center_number')}
          name={FormFields.CostCenterNumber}
          validate={[maxLength(50)]}
          disabled={endOfFundsDatePassed}
        />
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        columnGap={6}
        rowGap={4}
      >
        <PersonalDataField
          label={t('containers.beneficiaries.detail.floor')}
          name={FormFields.Floor}
          validate={[maxLength(50)]}
          disabled={endOfFundsDatePassed}
        />

        <PersonalDataField
          label={t('containers.beneficiaries.detail.department')}
          name={FormFields.Department}
          validate={[maxLength(50)]}
          disabled={endOfFundsDatePassed}
        />

        <PersonalDataField
          label={t('containers.beneficiaries.detail.region')}
          name={FormFields.Region}
          validate={[maxLength(50)]}
          disabled={endOfFundsDatePassed}
        />
      </Box>

      {!isAdd && (
        <PersonalDataField
          label={t('containers.beneficiaries.detail.create_data')}
          name={FormFields.CreatedAt}
          disabled={!isAdd || endOfFundsDatePassed}
          isDataField
        />
      )}
      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        columnGap={6}
        rowGap={4}
      >
        <Box>
          <TerminationDate
            showTerminationButton={!endOfFundsDatePassed}
            isAddMode={isAdd}
          />
          <Box
            mt={1}
            fontSize={sizeExtraSmall}
            id="add_beneficiary_lunch_benefit_per_month_subtext"
          >
            {t('containers.beneficiaries.detail.end_of_benefit_info')}
          </Box>
        </Box>

        {EoB && (
          <EndOfFundsField
            label={t('containers.beneficiaries.detail.end_of_founds')}
            name={FormFields.EndOfFunds}
            disabled
            isDataField
          />
        )}
      </Box>
    </Box>
  );
};
